// 퀴즈 유형별 설정 정의
export const QUIZ_TYPES = {
  SQL_WRITING: {
    code: '001',
    nameKey: 'challenge:quizType.sqlWriting',
    descKey: 'challenge:quizType.sqlWritingDesc',
    component: 'SqlWritingQuiz',
    config: {
      showTextArea: true,
      showPreValidation: true,
      showSubmit: true,
      allowMultipleSubmissions: true,
      validationRequired: false,
      maxQueryLength: 10000,
      timeLimit: null, // 시간 제한 없음
      scoring: {
        correctAnswer: 100,
        partialCredit: false
      }
    },
    dataStructure: {
      // 퀴즈 설명: 문제 내용 및 요구사항
      quizDescription: 'main_content',
      // 정답 SQL: 모범 답안 (참고용, 제출 후 표시)
      correctSql: 'reference_answer',
      // 정답 결과: 예상 결과 데이터 (검증용)
      correctResult: 'expected_output',
      // 퀴즈 힌트: 문제 해결 도움말
      quizHint: 'problem_solving_hint'
    }
  },

  MULTIPLE_CHOICE: {
    code: '002',
    nameKey: 'challenge:quizType.multipleChoice',
    descKey: 'challenge:quizType.multipleChoiceDesc',
    component: 'SingleChoiceQuiz',
    config: {
      showTextArea: false,
      showPreValidation: false,
      showSubmit: true,
      allowMultipleSubmissions: true, // 여러 번 제출 가능
      maxSubmissions: 3, // 최대 3회까지 제출 가능
      validationRequired: false,
      choiceCount: 4,
      timeLimit: 180, // 3분 제한
      scoring: {
        correctAnswer: 100,
        wrongAnswer: 0,
        partialCredit: false
      }
    },
    dataStructure: {
      // 퀴즈 설명: 문제 내용 (Markdown 형식)
      // 예: "## 문제\n다음 SQL 쿼리의 실행 결과는?\n\n```sql\nSELECT * FROM employees;\n```"
      quizDescription: 'question_markdown',
      // 정답 SQL: 각 선택지에 대한 설명 및 해설
      correctSql: 'choice_explanations',
      // 정답 결과: 선택지 목록 + 정답 여부 (JSON 형태)
      // 예: {"choices": [{"id": "1", "text": "...", "isAnswer": "Y"}, {"id": "2", "text": "...", "isAnswer": "N"}, ...]}
      correctResult: 'choices_with_answers_json',
      // 퀴즈 힌트: 문제 해결 힌트 (사용 안 함)
      quizHint: 'not_used'
    }
  },

  /**
   * 튜닝형 — **같은 결과를 더 싸게.** 정답/오답이 아니라 10/5/0 점이다.
   *
   * 채점은 서버가 한다(`/challenge/grade-tuning`): 모범답안과 제출을 각각 실측해
   * 논리 읽기를 견준다. 결과가 다르면 성능을 보기 전에 0점이다.
   *
   * **`maxSubmissions` 를 반드시 둔다.** 이 유형은 느린 SQL 을 DB 에서 **실제로** 끝까지
   * 돌린다 — 92만 행 전건 스캔을 무한정 다시 낼 수 있으면 남의 작업까지 느려진다.
   */
  TUNING_SQL: {
    code: '003',
    nameKey: 'challenge:quizType.tuningSql',
    descKey: 'challenge:quizType.tuningSqlDesc',
    component: 'TuningQuiz',
    config: {
      showTextArea: true,
      showPreValidation: false,   // 사전검증 대신 채점이 곧 실측이다
      showSubmit: true,
      allowMultipleSubmissions: true,
      maxSubmissions: 5,
      validationRequired: false,
      timeLimit: null,
      scoring: { full: 10, partial: 5, none: 0, partialCredit: true }
    },
    dataStructure: {
      quizDescription: 'problem_markdown',
      correctSql: 'answer_sql',                  // 모범답안 — 채점의 기준선
      correctResult: 'tuning_config_json',       // { slowSql, tolerance, baselineBuffers, ... }
      quizHint: 'hint_text'
    }
  },

  /** 인덱스 설계형 — 복제 표(PR_*)에 인덱스를 만들어 재고 **반드시 지운다**. 개인 연결이 필요하다. */
  TUNING_INDEX: {
    code: '004',
    nameKey: 'challenge:quizType.tuningIndex',
    descKey: 'challenge:quizType.tuningIndexDesc',
    component: 'TuningQuiz',
    config: {
      showTextArea: true,
      showPreValidation: false,
      showSubmit: true,
      allowMultipleSubmissions: true,
      maxSubmissions: 5,
      validationRequired: false,
      timeLimit: null,
      scoring: { full: 10, partial: 5, none: 0, partialCredit: true }
    },
    dataStructure: {
      quizDescription: 'problem_markdown',
      correctSql: 'not_used',                    // 답은 SQL 이 아니라 인덱스 설계다
      correctResult: 'tuning_config_json',       // { probeSql, answerColumns, targetTable, ... }
      quizHint: 'hint_text'
    }
  }
};

/** 튜닝형(003·004)인가 — 화면 분기가 이 한 곳을 본다. */
export const isTuningType = (code) => code === '003' || code === '004';

// 코드로 퀴즈 유형 찾기
export const getQuizTypeByCode = (code) => {
  return Object.values(QUIZ_TYPES).find(type => type.code === code) || QUIZ_TYPES.SQL_WRITING;
};

// 퀴즈 유형별 컴포넌트 이름 반환
export const getQuizComponentName = (quizTypeCode) => {
  const quizType = getQuizTypeByCode(quizTypeCode);
  return quizType.component;
};

// 퀴즈 유형별 설정 반환
export const getQuizConfig = (quizTypeCode) => {
  const quizType = getQuizTypeByCode(quizTypeCode);
  return quizType.config;
};

// 퀴즈 유형별 데이터 구조 정의 반환
export const getQuizDataStructure = (quizTypeCode) => {
  const quizType = getQuizTypeByCode(quizTypeCode);
  return quizType.dataStructure;
};

// 모든 퀴즈 유형 목록 반환
export const getAllQuizTypes = () => {
  return Object.values(QUIZ_TYPES);
};